import { cn } from '@cloud/shared/ui'
import { AlertTriangle, ExternalLink, Pause } from 'lucide-react'
import { useUser } from '@/providers/user-provider'

/**
 * Persistent banner that warns the user when their tenant is in a
 * non-active lifecycle state.
 *
 * It has three jobs:
 *
 *   1. Show the user that the workspace is paused or scheduled for
 *      deletion. Without this banner the experience is "everything
 *      looks normal until I click Save and get a confusing error
 *      toast" — the read-only middleware blocks the writes that
 *      cause the toasts.
 *
 *   2. Explain WHY writes are blocked and what to do about it.
 *      Different copy for paused vs pending_deletion, with a live
 *      countdown for the deletion case.
 *
 *   3. Get the user out — link them back to the marketing app's
 *      manage page where they can resume the workspace or cancel
 *      the deletion. We don't put any actions in the ERP itself
 *      because (a) those actions live on the marketing side and
 *      (b) keeping the ERP read-only-mode UI minimal is the point.
 *
 * Render conditions:
 *   - Status is paused or pending_deletion → render
 *   - Status is active or null → render nothing
 *   - User unauthenticated → render nothing (banner only inside
 *     the authenticated dashboard layout, which already gates on
 *     the user being signed in, but a defensive null check costs
 *     nothing)
 *
 * The banner is data-driven from `useUser()` — the whoami output
 * already includes `org.status` and `org.scheduledDeleteAt` so this
 * component doesn't need its own fetch. The 423 listener in
 * user-provider keeps the data fresh when the status changes
 * mid-session.
 */
export function WorkspaceStatusBanner() {
  const { current } = useUser()
  const status = current?.org.status ?? null
  if (status !== 'paused' && status !== 'pending_deletion') {
    return null
  }

  const tenantId = current?.org.id ?? ''
  const marketingBase = import.meta.env?.VITE_MARKETING_URL || 'http://localhost:3000'
  const manageUrl = tenantId
    ? `${marketingBase.replace(/\/+$/, '')}/account/workspaces/${encodeURIComponent(tenantId)}`
    : `${marketingBase.replace(/\/+$/, '')}/account`

  const isPaused = status === 'paused'
  const Icon = isPaused ? Pause : AlertTriangle
  const tone = isPaused
    ? 'border-warning/40 bg-warning/10 text-warning'
    : 'border-destructive/40 bg-destructive/10 text-destructive'

  const countdown =
    !isPaused && current?.org.scheduledDeleteAt
      ? formatCountdown(current.org.scheduledDeleteAt)
      : null

  const heading = isPaused ? 'This workspace is paused' : 'This workspace is scheduled for deletion'

  const body = isPaused
    ? 'You can still read data and run reports, but writes are blocked until an owner resumes the workspace.'
    : countdown
      ? `Read-only. ${countdown} until the database is permanently dropped. Cancel the deletion to keep this workspace.`
      : 'Read-only. Cancel the deletion to keep this workspace.'

  return (
    <div className={cn('border-b px-4 py-2.5 sm:px-6', tone)}>
      <div className="mx-auto flex max-w-7xl items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-semibold">{heading}.</span>{' '}
          <span className="opacity-90">{body}</span>
        </div>
        <a
          href={manageUrl}
          className={cn(
            'inline-flex flex-none items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold',
            'transition-colors hover:bg-background/40',
            isPaused ? 'border-warning/50' : 'border-destructive/50',
          )}
        >
          Manage
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      </div>
    </div>
  )
}

/**
 * Humanize the time remaining until a future timestamp. Mirrors
 * the helper in apps/marketing — kept as a private copy here
 * rather than shared because the marketing one lives inside a
 * route file and exporting it would couple the ERP to marketing.
 *
 * Doesn't auto-update — the banner is a static read of whoami
 * state and reloads on the next refresh. Day-precision is enough.
 */
function formatCountdown(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms) || ms <= 0) return 'Grace window expired'
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
  if (days >= 2) return `${days} days left`
  if (days === 1) return '1 day left'
  const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)))
  return `${hours} hour${hours === 1 ? '' : 's'} left`
}
